import type { SxProps, Theme } from '@mui/material';
import { viewportSafeWidthSx, viewportSafeColumnSx } from './viewportLayout';

/** Obsidian Gold design tokens (Creator / admin dashboard bento). */
export const OBSIDIAN = {
    /** App page shell — matches `theme.black` / profile & projects (not near-black #0e0e0e). */
    background: '#1c1c1d',
    surfaceContainer: '#1a1919',
    surfaceContainerLow: '#131313',
    surfaceContainerHigh: '#201f1f',
    surfaceContainerHighest: '#262626',
    surfaceVariant: '#262626',
    primary: '#ffc965',
    primaryDim: '#ecaa00',
    secondary: '#00fdc1',
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outline: '#767575',
    onPrimaryFixed: '#392700',
} as const;

export const OBSIDIAN_DISPLAY_FONT = '"Plus Jakarta Sans", Inter, sans-serif';
export const OBSIDIAN_BODY_FONT = '"Manrope", Inter, sans-serif';

let fontsEnsured = false;

export function ensureObsidianDashboardFonts(): void {
    if (fontsEnsured || typeof document === 'undefined') return;
    const id = 'obsidian-dashboard-fonts';
    if (document.getElementById(id)) {
        fontsEnsured = true;
        return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
        'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    fontsEnsured = true;
}

/** Applied to every bento grid cell so cards shrink inside flex/main layouts on mobile. */
export const bentoGridCellSx: SxProps<Theme> = {
    ...viewportSafeWidthSx,
};

export const bentoGridSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(12, minmax(0, 1fr))',
    },
    gap: { xs: 1.5, sm: 2, md: 3 },
    ...viewportSafeWidthSx,
};

/** Full-width shell inside AppLayout (overrides sidebar child `alignItems: center`). */
export const dashboardShellSx: SxProps<Theme> = {
    alignSelf: 'stretch',
    width: '100%',
    boxSizing: 'border-box',
    // Fill viewport below AppLayout header padding (xs/sm 50px, lg 80px)
    minHeight: { xs: 'calc(100dvh - 50px)', sm: 'calc(100dvh - 50px)', lg: 'calc(100dvh - 80px)' },
    ...viewportSafeColumnSx,
};

export const bentoCardSx: SxProps<Theme> = {
    backgroundColor: OBSIDIAN.surfaceContainer,
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    p: { xs: 2, sm: 3, md: 4 },
    minHeight: { xs: 140, sm: 180, md: 200 },
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
};

export const bentoLabelSx: SxProps<Theme> = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: OBSIDIAN.onSurfaceVariant,
    fontFamily: OBSIDIAN_BODY_FONT,
};

export const bentoValueSx: SxProps<Theme> = {
    fontSize: { xs: 28, sm: 36, md: 42 },
    fontWeight: 800,
    color: OBSIDIAN.onSurface,
    fontFamily: OBSIDIAN_DISPLAY_FONT,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
};

export const bentoValuePrimarySx: SxProps<Theme> = {
    ...bentoValueSx,
    color: OBSIDIAN.primary,
};

export const bentoSubtextSx: SxProps<Theme> = {
    fontSize: 13,
    color: OBSIDIAN.onSurfaceVariant,
    fontFamily: OBSIDIAN_BODY_FONT,
    mt: 0.5,
};

export const bentoManageValueSx: SxProps<Theme> = {
    fontSize: { xs: 24, sm: 28 },
    fontWeight: 700,
    color: OBSIDIAN.onSurface,
    fontFamily: OBSIDIAN_DISPLAY_FONT,
};

export type BentoColKey = 'full' | 'half' | 'third' | 'quarter' | 'revenue' | 'side' | 'wide';

const bentoColBase = (): SxProps<Theme> => ({ ...bentoGridCellSx });

export const bentoCol: Record<BentoColKey, SxProps<Theme>> = {
    full: { ...bentoColBase(), gridColumn: { xs: 'span 1', lg: 'span 12' } },
    half: { ...bentoColBase(), gridColumn: { xs: 'span 1', sm: 'span 1', lg: 'span 6' } },
    third: { ...bentoColBase(), gridColumn: { xs: 'span 1', sm: 'span 1', lg: 'span 4' } },
    quarter: { ...bentoColBase(), gridColumn: { xs: 'span 1', sm: 'span 1', lg: 'span 3' } },
    revenue: { ...bentoColBase(), gridColumn: { xs: 'span 1', lg: 'span 8' } },
    side: { ...bentoColBase(), gridColumn: { xs: 'span 1', sm: 'span 1', lg: 'span 4' } },
    wide: { ...bentoColBase(), gridColumn: { xs: 'span 1', lg: 'span 8' } },
};

/** Merge dashboard sx fragments for MUI `Box` without readonly spread issues. */
export const mergeBentoSx = (...styles: Array<SxProps<Theme> | false | undefined>): SxProps<Theme> =>
    styles.filter(Boolean) as SxProps<Theme>;

export const dashboardPageSx: SxProps<Theme> = {
    ...viewportSafeColumnSx,
    bgcolor: OBSIDIAN.background,
    color: OBSIDIAN.onSurface,
    fontFamily: OBSIDIAN_BODY_FONT,
};

export const dashboardTitleSx: SxProps<Theme> = {
    fontFamily: OBSIDIAN_DISPLAY_FONT,
    fontWeight: 800,
    fontSize: { xs: 32, sm: 40 },
    letterSpacing: '-0.03em',
    color: OBSIDIAN.onSurface,
    lineHeight: 1.15,
    mb: 1,
};

export const dashboardSubtitleSx: SxProps<Theme> = {
    fontFamily: OBSIDIAN_BODY_FONT,
    fontSize: 15,
    color: OBSIDIAN.onSurfaceVariant,
};
